import { VoiceProviders } from "Pages/agentBuilder/Pages/CustomAgentsGridPage/Hooks/queries";
import { image } from "Services/AssetManagementService";

import { ModelFamily, ModelGroupConfig, ModelMeta } from "./types";

export const MODEL_GROUP_CONFIG: Record<ModelFamily, ModelGroupConfig> = {
    "gpt-4o": {
        label: "OpenAI · GPT-4o",
        description: "Natural, real-time speech interaction.",
    },
    "gpt-4.1": {
        label: "OpenAI · GPT-4.1",
        description: "Structured workflows where compliance outweighs more natural conversation.",
    },
    "gpt-realtime": {
        label: "OpenAI · Realtime",
        description:
            "Ultra-low latency voice with native audio support, however less proven in production. Can only use with OpenAI voices.",
    },
    "gpt-5": {
        label: "OpenAI · GPT-5",
        description:
            "More controlled and consistent conversations with better instruction-following (especially with larger prompts and longer conversations). However, you may have to prompt more for conversational warmth in style guidelines.",
    },
    claude: {
        label: "Claude",
        description:
            "Anthropic's models excel at nuanced reasoning and analysis, but are higher latency and less conversational. Not right for most CX voice interactions. Could be useful for back-office interactions that require deeper reasoning.",
    },
    gemini: {
        label: "Gemini",
        description:
            "Google's models with comparable overall performance to OpenAI models across key benchmarks, but less conversational and less proven in production for CX voice conversations.",
    },
};

/** Gated by LaunchDarkly `show-gpt-5-6-models` (`showGpt56Models`). */
export const GPT_5_6_MODEL_VALUES = ["gpt-5.6-luna", "gpt-5.6-terra"] as const;

/** Gated by LaunchDarkly `show-gemini-3.6` (`showGemini36`). */
export const GEMINI_3_6_MODEL_VALUES = ["gemini-3.6-flash", "gemini-3.5-flash-lite"] as const;

export const MODEL_CATALOG: readonly ModelMeta[] = [
    // --- OpenAI · GPT-4o ---
    {
        label: "OpenAI 4o",
        value: "gpt-4o",
        icon: "open-ai",
        family: "gpt-4o",
        tooltip:
            "Best overall balance of intelligence, speed, and natural conversation. Ideal for most voice agents that need to sound human and handle complexity.",
        pricing: {
            voice: "+$0.044/min",
            sms: "+$0.037/msg",
            analysis: "+$0.040/interaction",
            disposition: "+$0.011/interaction",
        },
    },
    {
        label: "OpenAI 4o mini",
        value: "gpt-4o-mini",
        icon: "open-ai",
        family: "gpt-4o",
        isDefault: true,
        tooltip:
            "Fast and low-cost for simple, high-volume interactions; less strong on edge cases and reliable action invocation.",
        pricing: {
            voice: "Default",
            sms: "Default",
            analysis: "Default",
            disposition: "Default",
        },
    },

    // --- OpenAI · GPT-4.1 ---
    {
        label: "OpenAI 4.1",
        value: "gpt-4.1",
        icon: "open-ai",
        family: "gpt-4.1",
        tooltip:
            'Structured, reliable instruction-following and action use for workflow-driven agents. But less conversationally "natural" than 4o.',
        pricing: {
            voice: "+$0.039/min",
            sms: "+$0.033/msg",
            analysis: "+$0.020/interaction",
            disposition: "+$0.006/interaction",
        },
    },
    {
        label: "OpenAI 4.1 mini",
        value: "gpt-4.1-mini",
        icon: "open-ai",
        family: "gpt-4.1",
        tooltip:
            "A lightweight, faster version of 4.1 that's good for simple structured tasks and action invocation, but not ideal for highly dynamic conversations.",
        pricing: {
            voice: "+$0.010/min",
            sms: "+$0.008/msg",
            analysis: "+$0.008/interaction",
            disposition: "+$0.001/interaction",
        },
    },
    {
        label: "OpenAI 4.1 nano",
        value: "gpt-4.1-nano",
        icon: "open-ai",
        family: "gpt-4.1",
        tooltip:
            "Extremely fast and low-cost for basic conversations, but very limited reasoning and poor handling of anything non-trivial. Could be good just for reminder calls.",
        pricing: {
            voice: "+$0.000/min",
            sms: "+$0.000/msg",
            analysis: "+$0.000/interaction",
            disposition: "+$0.000/interaction",
        },
    },

    // --- OpenAI · Realtime ---
    {
        label: "OpenAI Realtime",
        value: "gpt-realtime",
        icon: "open-ai",
        family: "gpt-realtime",
        voiceOnly: true,
        pricing: { voice: "+$0.850/min" },
    },
    {
        label: "OpenAI Realtime 1.5",
        value: "gpt-realtime-1.5",
        icon: "open-ai",
        family: "gpt-realtime",
        voiceOnly: true,
        pricing: { voice: "+$0.850/min" },
    },
    {
        label: "OpenAI Realtime 2",
        value: "gpt-realtime-2",
        icon: "open-ai",
        family: "gpt-realtime",
        voiceOnly: true,
        pricing: { voice: "+$0.850/min" },
    },
    {
        label: "OpenAI Realtime Mini",
        value: "gpt-realtime-mini",
        icon: "open-ai",
        family: "gpt-realtime",
        voiceOnly: true,
        pricing: { voice: "+$0.267/min" },
    },

    // --- OpenAI · GPT-5 ---
    {
        label: "OpenAI 5",
        value: "gpt-5",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.034/min",
            sms: "+$0.028/msg",
            analysis: "+$0.016/interaction",
            disposition: "+$0.004/interaction",
        },
    },
    {
        label: "OpenAI 5 mini",
        value: "gpt-5-mini",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.006/min",
            sms: "+$0.005/msg",
            analysis: "+$0.005/interaction",
            disposition: "+$0.000/interaction",
        },
    },
    {
        label: "OpenAI 5 nano",
        value: "gpt-5-nano",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.000/min",
            sms: "+$0.000/msg",
            analysis: "+$0.000/interaction",
            disposition: "+$0.000/interaction",
        },
    },
    {
        label: "OpenAI 5.1",
        value: "gpt-5.1",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.034/min",
            sms: "+$0.028/msg",
            analysis: "+$0.016/interaction",
            disposition: "+$0.004/interaction",
        },
    },
    {
        label: "OpenAI 5.2",
        value: "gpt-5.2",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.048/min",
            sms: "+$0.039/msg",
            analysis: "+$0.023/interaction",
            disposition: "+$0.006/interaction",
        },
    },
    {
        label: "OpenAI 5.4",
        value: "gpt-5.4",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.075/min",
            sms: "+$0.067/msg",
            analysis: "+$0.037/interaction",
            disposition: "+$0.007/interaction",
        },
    },
    {
        label: "OpenAI 5.4 Mini",
        value: "gpt-5.4-mini",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.018/min",
            sms: "+$0.016/msg",
            analysis: "+$0.009/interaction",
            disposition: "+$0.002/interaction",
        },
    },
    {
        label: "OpenAI 5.4 Nano",
        value: "gpt-5.4-nano",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.000/min",
            sms: "+$0.000/msg",
            analysis: "+$0.000/interaction",
            disposition: "+$0.000/interaction",
        },
    },
    {
        label: "OpenAI 5.6 Luna",
        value: "gpt-5.6-luna",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.000/min",
            sms: "+$0.000/msg",
            analysis: "+$0.002/interaction",
            disposition: "+$0.000/interaction",
        },
    },
    {
        label: "OpenAI 5.6 Terra",
        value: "gpt-5.6-terra",
        icon: "open-ai",
        family: "gpt-5",
        pricing: {
            voice: "+$0.058/min",
            sms: "+$0.053/msg",
            analysis: "+$0.069/interaction",
            disposition: "+$0.006/interaction",
        },
    },

    // --- Anthropic / Claude ---
    {
        label: "Claude 4.5 Sonnet",
        value: "claude-sonnet-4-5",
        icon: "anthropic",
        family: "claude",
        pricing: { voice: "+$0.091/min", sms: "+$0.082/msg" },
    },
    {
        label: "Claude 4.5 Haiku",
        value: "claude-haiku-4-5",
        icon: "anthropic",
        family: "claude",
        pricing: { voice: "+$0.026/min", sms: "+$0.023/msg" },
    },
    {
        label: "Claude 4.6 Sonnet",
        value: "claude-sonnet-4-6",
        icon: "anthropic",
        family: "claude",
        pricing: { voice: "+$0.091/min", sms: "+$0.082/msg" },
    },
    // --- Google Gemini ---
    {
        label: "Gemini 3.1 Flash Lite",
        value: "gemini-3.1-flash-lite-preview",
        icon: "google-gemini",
        family: "gemini",
        pricing: { voice: "+$0.001/min", sms: "+$0.001/msg" },
    },
    {
        label: "Gemini 3.5 Flash",
        value: "gemini-3.5-flash",
        icon: "google-gemini",
        family: "gemini",
        pricing: { voice: "+$0.038/min", sms: "+$0.019/msg" },
    },
    {
        label: "Gemini 3.5 Flash Lite",
        value: "gemini-3.5-flash-lite",
        icon: "google-gemini",
        family: "gemini",
        pricing: { voice: "+$0.003/min", sms: "+$0.001/msg" },
        excludeFromAnalysis: true,
    },
    {
        label: "Gemini 3.6 Flash",
        value: "gemini-3.6-flash",
        icon: "google-gemini",
        family: "gemini",
        pricing: { voice: "+$0.038/min", sms: "+$0.034/msg" },
        excludeFromAnalysis: true,
    },
] as const;

const MODEL_LABEL_MAP = new Map(MODEL_CATALOG.map((m) => [m.value, m.label]));

/** Admin override (e.g. Baseten) model value → label. Keep in sync with LLM > Baseten > Model options in AIAdminConfigsPanel/schema.ts */
const OVERRIDE_MODEL_LABEL_MAP: Record<string, string> = {
    "zai-org/GLM-4.7": "GLM 4.7",
};

export const getModelLabel = (value: string): string =>
    MODEL_LABEL_MAP.get(value) ?? OVERRIDE_MODEL_LABEL_MAP[value] ?? value;

/**
 * Whether a stored model can still be picked in the builder. Versions are immutable history, so
 * older ones can reference models that have since been retired from the catalog; restoring those
 * replaces the model with `DEFAULT_LLM_MODEL_VALUE` server-side.
 */
export const isSelectableModel = (value?: string | null): boolean =>
    !!value && (MODEL_LABEL_MAP.has(value) || value in OVERRIDE_MODEL_LABEL_MAP);

// we need to think about this a bt more.
export const AI_AGENT_QUERY_PARAMS = {
    // this the param we are updating
    TAB: "tab",
    CHANNEL: "channel",
    TYPE: "type",
    BUILD: "build",
    VARIANT_ID: "variantId",
    // these are tabs / values we are updating to
    SIMULATIONS: {
        TAB: "simulation",
        ID: "testRunId",
        SIMULATION_ID: "simulationId",
    },
    TESTING: "testing",
    TESTING_VALUES: {
        LOGIC: "logic",
        AUDIO: "audio",
    },
} as const;

export const AI_AGENT_BUILD_MODES = {
    SCRATCH: "scratch",
    TEMPLATE: "template",
} as const;

export const CustomAgentType = {
    SingleState: "single_state",
    MultiState: "multi_state",
} as const;

export const MULTI_SELECT_VARIABLE_TYPE_VALUE = "multi_enum";

export const CUSTOM_AI_VARIABLE_TYPE_OPTIONS = [
    { label: "String", value: "string" },
    { label: "Number", value: "number" },
    { label: "Boolean", value: "boolean" },
    { label: "Single Select", value: "enum" },
    { label: "Multi Select", value: MULTI_SELECT_VARIABLE_TYPE_VALUE },
];

/**
 * Multi Select (`multi_enum`) variable type is gated by LaunchDarkly
 * `show-multi-select-variable-type` (`showMultiSelectVariableType`). When the flag is off the
 * option is hidden from the data-type dropdowns; variables already saved as `multi_enum`
 * continue to render and run. Pass the field's current value so a persisted `multi_enum`
 * keeps its matching option/label even while the flag hides it for new selections.
 */
export function getCustomAiVariableTypeOptions(
    showMultiSelectVariableType: boolean | undefined,
    currentValue?: string
) {
    if (showMultiSelectVariableType === true || currentValue === MULTI_SELECT_VARIABLE_TYPE_VALUE) {
        return CUSTOM_AI_VARIABLE_TYPE_OPTIONS;
    }
    return CUSTOM_AI_VARIABLE_TYPE_OPTIONS.filter((option) => option.value !== MULTI_SELECT_VARIABLE_TYPE_VALUE);
}

export const CUSTOM_AI_VARIABLE_VALUE_TYPE_OPTIONS = [
    { label: "String", value: "string" },
    { label: "Number", value: "number" },
];

export const CARTESIA_VOICE_PROVIDER: VoiceProviders = "cartesia";
export const ELEVENLABS_VOICE_PROVIDER: VoiceProviders = "elevenlabs";
export const OPENAI_VOICE_PROVIDER: VoiceProviders = "openai";
export const RIME_VOICE_PROVIDER: VoiceProviders = "rime";

const REALTIME_MODELS = new Set(["gpt-realtime", "gpt-realtime-1.5", "gpt-realtime-2", "gpt-realtime-mini"]);
export const isRealtimeModelValue = (model: string | undefined): boolean =>
    model !== undefined && REALTIME_MODELS.has(model);

export const OPENAI_REALTIME_VOICES = [
    { label: "Alloy", value: "alloy", imageUrl: image("templateAgents/ai_olivia.webp") },
    { label: "Ash", value: "ash", imageUrl: image("templateAgents/ai_mike.webp") },
    { label: "Ballad", value: "ballad", imageUrl: image("templateAgents/ai_adrian.webp") },
    { label: "Cedar", value: "cedar", imageUrl: image("templateAgents/ai_jeff.webp") },
    { label: "Coral", value: "coral", imageUrl: image("templateAgents/ai_jenny.webp") },
    { label: "Echo", value: "echo", imageUrl: image("templateAgents/ai_ethan.webp") },
    { label: "Marin", value: "marin", imageUrl: image("templateAgents/ai_emily.webp") },
    { label: "Sage", value: "sage", imageUrl: image("templateAgents/ai_jenny.webp") },
    { label: "Shimmer", value: "shimmer", imageUrl: image("templateAgents/ai_emily.webp") },
    { label: "Verse", value: "verse", imageUrl: image("templateAgents/ai_adrian.webp") },
] as const;

export const INCOMPATIBLE_PROVIDERS: Record<VoiceProviders, VoiceProviders[]> = {
    cartesia: [],
    openai: ["rime"],
    elevenlabs: [],
    deepgram: [],
    play: [],
    rime: ["openai"],
};

export const AI_METRIC_OPTIONS = {
    transferRate: "transfer-rate",
    scheduledCallbackRate: "scheduled-callback-rate",
    containmentRate: "containment-rate",
    abandonRate: "abandon-rate",
    plus60SecondCalls: "plus-60-second-calls",
    scheduledRate: "scheduled-rate",
    responseTime: "response-time",
    successRate: "success-rate",
    resolutionTime: "resolution-time",
    engagementRate: "engagement-rate",
    firstContactResolution: "first-contact-resolution",
    costPerInteraction: "cost-per-interaction",
    revenuePerLead: "revenue-per-lead",
    csat: "csat",
    nps: "nps",
    qualificationRate: "qualification-rate",
};

const VOICE_METRIC_OPTIONS = [
    { label: "Transfer Rate", value: AI_METRIC_OPTIONS.transferRate },
    { label: "Scheduled Callback Rate", value: AI_METRIC_OPTIONS.scheduledCallbackRate },
    { label: "Containment Rate", value: AI_METRIC_OPTIONS.containmentRate },
    { label: "Abandon Rate", value: AI_METRIC_OPTIONS.abandonRate },
    { label: "Calls > 60 Seconds", value: AI_METRIC_OPTIONS.plus60SecondCalls },
];

const SMS_METRIC_OPTIONS = [
    { label: "Scheduled Rate", value: AI_METRIC_OPTIONS.scheduledRate },
    { label: "Response Time", value: AI_METRIC_OPTIONS.responseTime },
    { label: "Success Rate", value: AI_METRIC_OPTIONS.successRate },
    { label: "Resolution Time", value: AI_METRIC_OPTIONS.resolutionTime },
    { label: "Engagement Rate", value: AI_METRIC_OPTIONS.engagementRate },
];

const EXTRA_METRIC_OPTIONS = [
    { label: "First Contact Resolution", value: AI_METRIC_OPTIONS.firstContactResolution },
    { label: "Cost per Interaction", value: AI_METRIC_OPTIONS.costPerInteraction },
    { label: "Revenue per Lead", value: AI_METRIC_OPTIONS.revenuePerLead },
    { label: "CSAT", value: AI_METRIC_OPTIONS.csat },
    { label: "NPS", value: AI_METRIC_OPTIONS.nps },
    { label: "Qualification Rate", value: AI_METRIC_OPTIONS.qualificationRate },
];

/**
 * Metric options for the "Metrics to Track" select, gated by agent channel: phone agents get the
 * voice metrics, SMS agents get the messaging metrics, and both include the shared extras. Sorted
 * alphabetically by label.
 */
export function getAgentMetricsOptions(isPhone: boolean): { label: string; value: string }[] {
    return (
        isPhone ? [...VOICE_METRIC_OPTIONS, ...EXTRA_METRIC_OPTIONS] : [...SMS_METRIC_OPTIONS, ...EXTRA_METRIC_OPTIONS]
    ).sort((a, b) => a.label.localeCompare(b.label));
}

export const AI_ACTION_NAME_OPTIONS = {
    endCall: "EndCall",
    internalTransfer: "InternalTransfer",
    externalTransfer: "ExternalTransfer",
    sendWebhook: "SendWebhook",
    checkHoursTemplate: "CheckHoursTemplate",
    scheduleCallback: "ScheduleCallback",
    checkCalcom: "CheckCal.com",
    bookCalcom: "BookCal.com",
    customAction: "CustomAction",
    hostedFunction: "HostedFunction",
    gatherDate: "GatherDate",
    voicemailDrop: "VoicemailDrop",
    startRecording: "StartRecording",
    stopRecording: "StopRecording",
    transfer: "Transfer",
    pressDigit: "PressDigit",
    setUserData: "SetUserData",
    setContactData: "SetContactData",
    authenticateContact: "AuthenticateContact",
    chainActions: "ChainActions",
};

export const AI_ACTION_NAME_OPTIONS_LABELS = {
    EndCall: "End Call",
    InternalTransfer: "Internal Transfer",
    ExternalTransfer: "External Transfer",
    SendWebhook: "Send Webhook",
    CheckHoursTemplate: "Check Hours Template",
    ScheduleCallback: "Schedule Callback",
    ["CheckCal.com"]: "Check Cal.com",
    ["BookCal.com"]: "Book Cal.com",
    CustomAction: "Custom Action",
    HostedFunction: "Hosted Function",
    GatherDate: "Gather Date/Time",
    VoicemailDrop: "Voicemail Drop",
    StartRecording: "Start Recording",
    StopRecording: "Stop Recording",
    Transfer: "Transfer Call",
    PressDigit: "Press Digit",
    SetUserData: "Set User Data",
    SetContactData: "Set Contact Data",
    AuthenticateContact: "Authenticate Contact",
    ChainActions: "Action Sequence",
};

export const AI_ACTION_TYPE_OPTIONS = {
    endCall: "end_call",
    internalTransfer: "internal_transfer",
    externalTransfer: "external_transfer",
    sendWebhook: "send_webhook",
    checkHoursTemplate: "check_hours",
    scheduleCallback: "schedule_callback",
    checkAvailability: "check_availability",
    checkCalcom: "check_cal.com",
    bookCalcom: "book_cal.com",
    customAction: "custom_action",
    hostedFunction: "regal_function",
    gatherDate: "gather_date",
    voicemailDrop: "voicemail_drop",
    startRecording: "start_recording",
    stopRecording: "stop_recording",
    transfer: "transfer",
    pressDigit: "press_digit",
    setUserData: "set_user_data",
    setContactData: "set_contact_data",
    authenticateContact: "authenticate_contact",
    chainActions: "chain_actions",
} as const;

export const AI_BACKGROUND_SOUND_OPTIONS = {
    NONE: "none",
    COFFEE_SHOP: "coffee-shop",
    CALL_CENTER: "call-center",
};

export const AI_DATA_EXTRACTION_TYPE_OPTIONS = {
    TEXT: "text",
    TEXT_WITH_OPTIONS: "textWithOptions",
    BOOLEAN: "boolean",
    NUMBER: "number",
};

export const AI_DATA_EXTRACTION_FIELDS = {
    name: "name",
    type: "type",
    description: "description",
    options: "options",
};

export const AI_KNOWLEDGE_BASE_FIELDS = {
    name: "name",
    id: "id",
};

export { VOICE_LANGUAGE_OPTIONS } from "Utils/voiceLanguageOptions";

export const LLM_OPTION_TOOLTIP = "Price shown is amount above default price";

/** The value of the model marked `isDefault` in MODEL_CATALOG (i.e. the zero-cost baseline). */
export const DEFAULT_LLM_MODEL_VALUE = MODEL_CATALOG.find((m) => m.isDefault)?.value ?? "gpt-4o-mini";

export { PREDEFINED_ACTION_ICON_MAP } from "Utils/aiActionIcons";

export const CUSTOM_ACTION_DEFAULT_TIMEOUT_MS = 10_000;
